import {
  isRetainedClosingFeeLineItem,
  type RetainedClosingFeeBasis
} from './wireReconciliation';

export interface NormalizableLoanRevenueRow {
  id: string;
  amount?: number | string | null;
  reimbursable?: boolean | null;
  warehouse_retained_amount?: number | string | null;
  nets_to_warehouse_advance?: boolean | null;
}

export interface NormalizableRevenueState<
  TLoan extends NormalizableLoanRevenueRow = NormalizableLoanRevenueRow
> {
  loan: TLoan[];
}

function toFiniteNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function shouldNormalizeRetainedLoanRevenueRow(
  row: NormalizableLoanRevenueRow,
  retainedClosingFeeBasis: RetainedClosingFeeBasis = 'legacy'
): boolean {
  const retainedAmount = toFiniteNumber(row.warehouse_retained_amount);
  if (retainedAmount <= 0 || row.reimbursable === true) {
    return false;
  }

  if (row.nets_to_warehouse_advance === true) {
    return true;
  }
  if (row.nets_to_warehouse_advance === false) {
    return false;
  }

  return isRetainedClosingFeeLineItem({
    category: 'loan_revenue',
    line_type: row.id
  }, retainedClosingFeeBasis);
}

export function normalizeRetainedLoanRevenueRow<TLoan extends NormalizableLoanRevenueRow>(
  row: TLoan,
  retainedClosingFeeBasis: RetainedClosingFeeBasis = 'legacy'
): TLoan {
  if (!shouldNormalizeRetainedLoanRevenueRow(row, retainedClosingFeeBasis)) {
    return row;
  }

  return {
    ...row,
    amount: toFiniteNumber(row.amount) + toFiniteNumber(row.warehouse_retained_amount),
    warehouse_retained_amount: 0
  };
}

export function normalizeRevenueStateForDraft<TRevenue extends NormalizableRevenueState>(
  revenue: TRevenue,
  retainedClosingFeeBasis: RetainedClosingFeeBasis = 'legacy'
): TRevenue {
  let changed = false;
  const loan = revenue.loan.map(row => {
    const normalized = normalizeRetainedLoanRevenueRow(row, retainedClosingFeeBasis);
    if (normalized !== row) {
      changed = true;
    }
    return normalized;
  });

  if (!changed) {
    return revenue;
  }

  return {
    ...revenue,
    loan
  };
}
